import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { gorseliPaylas, kodGorseliUret } from '../core/kodGorseli.js';
import { panoyaKopyala } from '../core/pano.js';

export interface QrKod {
  etiket: string;
  deger: string;
}

interface Props {
  /** Tek kod ya da bir eşleştirmenin iki kodu. */
  kodlar: QrKod[];
  onClose: () => void;
  /** Verilirse silme butonu görünür. Kayıt listesinde kullanılır. */
  onDelete?: () => Promise<void>;
  /** Paylaşılan görselin altına yazılır (kategori adı gibi). */
  altNot?: string;
  /** Kayda ait ek metin (barkodsuz kayıtta açıklama). */
  altBilgi?: string;
}

/** Birlikte kopyalamada iki kodun arasına giren ayraç. */
const AYRAC = ' | ';

/**
 * Kod(lar)ın QR görüntüsü ve panoya kopyalama.
 *
 * İki kullanım yeri var: okutulmayan listesindeki tek barkod, ve arama
 * sonucundaki bir eşleştirmenin iki kodu. İkinci durumda üstteki satırlardan
 * biri seçilir, QR ona göre üretilir — iki QR'ı yan yana küçültmek yerine
 * seçileni büyük göstermek okutmayı kolaylaştırır.
 *
 * Eski projedeki `qrview()` (araclar/qrview.dart) karşılığı, ama orada
 * yalnızca tek kod gösterilebiliyordu.
 */
export function QrDialog({ kodlar, onClose, onDelete, altNot, altBilgi }: Props) {
  const [secili, setSecili] = useState(0);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [kopyalanan, setKopyalanan] = useState<string | null>(null);
  const [silOnay, setSilOnay] = useState(false);
  const [siliniyor, setSiliniyor] = useState(false);
  const [silHata, setSilHata] = useState<string | null>(null);
  const [paylasiliyor, setPaylasiliyor] = useState(false);
  const [paylasimNotu, setPaylasimNotu] = useState<string | null>(null);

  const aktif = kodlar[secili] ?? kodlar[0];

  const kopyala = useCallback(async (deger: string) => {
    if (await panoyaKopyala(deger)) {
      setKopyalanan(deger);
      setTimeout(() => setKopyalanan((k) => (k === deger ? null : k)), 1800);
    }
  }, []);

  useEffect(() => {
    if (!aktif) return;
    let iptal = false;

    void QRCode.toDataURL(aktif.deger, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#1c2530', light: '#ffffff' },
    })
      .then((url) => {
        if (!iptal) setDataUrl(url);
      })
      .catch(() => {
        if (!iptal) setDataUrl(null);
      });

    return () => {
      iptal = true;
    };
  }, [aktif]);

  /** Tek kod varsa açılır açılmaz kopyalanır; iki kod varsa hangisi olduğu belirsiz. */
  useEffect(() => {
    if (kodlar.length === 1 && kodlar[0]) void kopyala(kodlar[0].deger);
  }, [kodlar, kopyala]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const birlesik = kodlar.map((k) => k.deger).join(AYRAC);
  /** Görsel yalnızca QR barkodu gösterirken paylaşılabilir. */
  const paylasilabilir = kodlar.length > 1 && secili === 0;

  /**
   * Metinler kaydın türüne göre değişiyor: iki kodlu kayıt bir eşleştirme,
   * tek kodlu ya da kodsuz kayıt (barkodsuz kategori) yalnızca bir kayıt.
   * "Eşleştirmeyi sil" demek orada yanlış bilgi verirdi.
   */
  const eslestirme = kodlar.length > 1;

  // Kodsuz kayıt da bu pencereden yönetiliyor; erken çıkılırsa silme
  // düğmesine ulaşmanın yolu kalmıyor. QR bölümü aşağıda ayrıca korunuyor.

  return (
    <div className="onay-katman" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="qr-kutu" onClick={(e) => e.stopPropagation()}>
        <div className="qr-kodlar">
          {kodlar.map((k, i) => (
            <button
              key={k.deger}
              className={`qr-kod-satir ${i === secili ? 'secili' : ''}`}
              onClick={() => {
                setSecili(i);
                void kopyala(k.deger);
              }}
            >
              <span className="qr-kod-etiket">{k.etiket}</span>
              <span className="qr-kod-deger">{k.deger}</span>
              <span className="qr-kod-durum">
                {kopyalanan === k.deger ? '✓ kopyalandı' : 'kopyala'}
              </span>
            </button>
          ))}
        </div>

        {/*
          Kodu olmayan kayıtlar da bu pencereden yönetiliyor (barkodsuz
          kategoride yalnızca fotoğrafla açılmış kayıtlar). QR çizilemez ama
          pencere açılmalı, yoksa kaydı silmenin bir yolu kalmıyor.
        */}
        {aktif ? (
          <div className="qr-gorsel">
            {dataUrl ? (
              <img src={dataUrl} alt={`${aktif.deger} QR kodu`} />
            ) : (
              <span className="qr-bekle">QR oluşturuluyor…</span>
            )}
          </div>
        ) : (
          <p className="qr-kodsuz">Bu kaydın ürün kodu yok.</p>
        )}

        {altBilgi && <p className="qr-altbilgi">{altBilgi}</p>}

        {kodlar.length > 1 && (
          <p className="qr-ipucu">QR kodu: {aktif?.etiket}</p>
        )}

        {kodlar.length > 1 && (
          <button
            className="btn-birlikte"
            onClick={() => void kopyala(kodlar.map((k) => k.deger).join(AYRAC))}
          >
            {kopyalanan === birlesik ? '✓ İkisi de kopyalandı' : 'İkisini birlikte kopyala'}
          </button>
        )}

        {/*
          Görseldeki QR her zaman barkodu taşır. Kullanıcı ürün koduna geçip
          QR'ı değiştirdiyse buton gizlenir — aksi halde ekranda gördüğünden
          farklı bir QR paylaşılmış olurdu.
        */}
        {paylasilabilir && (
          <button
            className="btn-paylas"
            disabled={paylasiliyor}
            onClick={async () => {
              setPaylasiliyor(true);
              setPaylasimNotu(null);
              try {
                const blob = await kodGorseliUret({
                  qrKodu: kodlar[0]!.deger,
                  satirlar: kodlar,
                  altNot,
                });
                /**
                 * Açıklama olarak barkod gönderiliyor: karşı taraf resmi
                 * açmadan da kodu görebilsin, kopyalayabilsin.
                 */
                const sonuc = await gorseliPaylas(
                  blob,
                  `${kodlar[0]!.deger}.png`,
                  kodlar[0]!.deger,
                );
                if (sonuc === 'indirildi') setPaylasimNotu('Görsel indirildi');
              } catch {
                setPaylasimNotu('Görsel oluşturulamadı');
              } finally {
                setPaylasiliyor(false);
              }
            }}
          >
            {paylasiliyor ? 'Hazırlanıyor…' : 'Resim olarak paylaş'}
          </button>
        )}

        {paylasimNotu && <p className="qr-ipucu">{paylasimNotu}</p>}

        {silHata && <div className="hata-kutu">{silHata}</div>}

        {onDelete && silOnay ? (
          /**
           * Silme onayı diyalog içinde açılır. Ayrı bir pencere yerine
           * burada sorulması, hangi kaydın silineceğinin ekranda görünür
           * kalmasını sağlar.
           */
          <div className="sil-onay">
            <p>
              {eslestirme
                ? 'Bu eşleştirme silinecek. Kodlar yeniden eşleştirilebilir hale gelir.'
                : 'Bu kayıt silinecek.'}
            </p>
            <div className="onay-butonlar">
              <button
                className="btn-iptal"
                onClick={() => setSilOnay(false)}
                disabled={siliniyor}
              >
                Vazgeç
              </button>
              <button
                className="btn-devam"
                disabled={siliniyor}
                onClick={async () => {
                  setSiliniyor(true);
                  setSilHata(null);
                  try {
                    await onDelete();
                    onClose();
                  } catch {
                    setSilHata('Kayıt silinemedi');
                    setSiliniyor(false);
                  }
                }}
              >
                {siliniyor ? 'Siliniyor…' : 'Sil'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {onDelete && (
              <button className="btn-sil" onClick={() => setSilOnay(true)}>
                {eslestirme ? 'Eşleştirmeyi sil' : 'Kaydı sil'}
              </button>
            )}
            <button className="btn-kapat" onClick={onClose}>
              Kapat
            </button>
          </>
        )}
      </div>
    </div>
  );
}
